<template>
  <div
    v-bind="$attrs"
    class="flex flex-col items-center gap-1.5 text-center cursor-pointer"
    :class="compact ? 'w-[135px]' : 'w-[100px]'"
    @click="showActivityModal = true"
  >
    <div
      class="flex flex-row justify-center w-full relative"
      :class="compact ? 'h-[70px] items-start pt-[9px]' : ''"
    >
      <div
        class="absolute top-0 inline-flex items-center justify-center z-10 self-start aspect-square rounded-full text-center font-serif text-sm"
        :class="[
          compact ? 'text-text-on-brand' : 'text-text',
          compact
            ? 'size-8 border-[3px] border-dark left-[18px]'
            : 'size-10 border-4 border-bg -left-1',
          {
            'bg-gold-coin': badgeStyle === 'gold',
            'bg-orange-coin': badgeStyle === 'orange',
            'bg-fill-silver': badgeStyle === 'silver',
            'bg-fill-bronze': badgeStyle === 'bronze',
            'bg-fill-brand': badgeStyle === 'primary',
            'bg-fill-warning': badgeStyle === 'orange',
          },
        ]"
      >
        {{ badgeLabel }}
      </div>

      <AvatarRoot
        as="div"
        class="@container-[size] relative inline-block"
        :class="[
          compact
            ? 'h-[60px] border-[3px] border-dark rounded-full'
            : 'h-[70px]',
          {
            placeholder: !user.avatar,
            'aspect-square': aspect === 'square',
            'aspect-[34/55]': aspect === 'rect',
          },
        ]"
      >
        <AvatarImage
          :src="user.avatar ?? ''"
          class="h-[100cqh] w-[100cqw]"
          :class="{
            'bg-cover bg-center rounded-full inline-flex justify-center items-stretch object-cover':
              user.avatar,
          }"
        />

        <AvatarFallback
          :delay-ms="1"
          class="h-[100cqh] w-[100cqw] text-lg capitalize rounded-full inline-flex justify-center items-center"
          :style="{
            'background-color': color ?? 'var(--fill-critical)',
          }"
        >
          {{ user.username[0] }}
        </AvatarFallback>
      </AvatarRoot>

      <div
        class="absolute bottom-0"
        :class="compact ? 'right-[18px]' : '-right-1'"
      >
        <CountryBadge
          :icon="user.country_iso"
          class="border-solid"
          :class="
            compact
              ? '!size-8 !border-[6px] border-dark'
              : '!size-10 border-4 border-brand-bg-main'
          "
        />
      </div>
    </div>

    <div class="w-full">
      <div v-if="!hideName" class="flex flex-col items-center w-full">
        <UsernameWithBadge
          :username="user.username"
          :is-vip="hasPaidMembership"
          :on-light-surface="lightSurface"
        />
      </div>

      <slot></slot>
    </div>
  </div>

  <ActivityModal
    v-model:is-open="showActivityModal"
    :loading="userActivityLoading"
    :user="user"
    :user-activities-with-summary="userActivitiesWithSummary"
    :is-vip="hasPaidMembership"
  />
</template>

<script lang="ts">
import { graphql as gql } from "@/generated";
import type { ResultOf } from "@graphql-typed-document-node/core";

export const UserAvatarAndNameFragment = gql(/* GraphQL */ `
  fragment UserAvatarAndName on User {
    id
    username
    avatar
    country_iso
  }
`);

export type UserAvatarAndName = ResultOf<typeof UserAvatarAndNameFragment>;
</script>

<script setup lang="ts">
import { AvatarFallback, AvatarImage, AvatarRoot } from "reka-ui";
import CountryBadge from "../badge/country-badge.vue";
import { computed, defineAsyncComponent, ref } from "vue";
import { useUserColor } from "@/composables/use-user-color";
import { useQuery } from "@vue/apollo-composable";
import { GetUserActivitiesWithSummaryDocument } from "@/generated/graphql";
import { useChannelStore } from "@/stores/channel.store";
import { getStartOfMonthWithOffset } from "@/utils/date";
import { AppThemeKind, useThemeStore } from "@/stores/theme.store";
import UsernameWithBadge from "../me/username-with-badge.vue";

// Lazy load
const ActivityModal = defineAsyncComponent(
  () => import("../leaderboard/activity-modal.vue"),
);

const props = withDefaults(
  defineProps<{
    userFragment: UserAvatarAndName;
    hasPaidMembership: boolean;
    aspect: "square" | "rect";
    badgeStyle: "gold" | "silver" | "bronze" | "primary" | "orange";
    badgeLabel: string | number;
    /**
     * The new channel hero panel renders this row inside a
     * LIGHT `surface-secondary` panel at the Figma's compact metrics — a 60px
     * avatar with 32px coin/flag badges (white rings) and a 135px column,
     * versus the legacy 70px / 40px / 100px sizing the leaderboard banner uses.
     * Default false keeps every legacy/dark-surface consumer byte-identical.
     */
    compact?: boolean;
    /**
     * Routes the username to the light-surface treatment (theme `text-text`
     * instead of always-white `text-text-on-brand`, no brand-red VIP pill).
     * Default false preserves the dark-banner appearance.
     */
    lightSurface?: boolean;
    /**
     * Suppresses the username row entirely. The MEANWHILE multiplier item in the
     * Figma master replaces the name with the red "Max" badge (rendered by the
     * caller in the default slot), so the always-on username must be hidden for
     * that variant. Default false renders the username as before.
     */
    hideName?: boolean;
  }>(),
  {
    compact: false,
    lightSurface: false,
    hideName: false,
  },
);

const user = computed(() => props.userFragment);

const color = useUserColor(computed(() => user.value.id));

const showActivityModal = ref(false);
const channelStore = useChannelStore();
const themeStore = useThemeStore();

const {
  result: getUserActivitiesWithSummaryResult,
  loading: userActivityLoading,
} = useQuery(
  GetUserActivitiesWithSummaryDocument,
  computed(() => ({
    channel_id: channelStore.channelSlug!,
    user_id: user.value.id,
    date: getStartOfMonthWithOffset(
      channelStore.channelSlug ?? "",
    ).toISOString(),
    community_id:
      themeStore.currentTheme?.kind === AppThemeKind.community
        ? themeStore.currentTheme?.id
        : undefined,
  })),
  {
    enabled: computed(
      () => !!channelStore.channelSlug && !!showActivityModal.value,
    ),
  },
);
const userActivitiesWithSummary = computed(
  () => getUserActivitiesWithSummaryResult.value?.userActivitiesWithSummary,
);
</script>

<style>
.bg-gold-coin {
  background-image: url("/contests/gold-coin.svg");
  background-size: 100% 100%;
  background-repeat: no-repeat;
  background-position: center;
}

.bg-orange-coin {
  background-image: url("/contests/orange-coin.svg");
  background-size: 100% 100%;
  background-repeat: no-repeat;
  background-position: center;
}
</style>
